"use client";

import styles from "./index.module.scss";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import cx from "classnames";
import { ILoginFieldInput, LoginProps } from "@/domains/auth/types";
import { useAuthLogin } from "@/domains/auth/hooks/user-auth-login";
import { useStore } from "@/hooks/use-store";
import { PhoneInput } from "@/components/UI/PhoneInput/PhoneInput";
import { getProfile } from "@/domains/profile/actions";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import ToggleEmailPhone from "@/components/UI/ToggleEmailPhone";

export default function Login({ onShowTab }: LoginProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<ILoginFieldInput>({
    mode: "all",
  });

  const t = useTranslations("AuthModal");
  const tValidation = useTranslations("Validation");
  const { modal, user } = useStore();
  const [country, setCountry] = useState<string>("UA");
  const [errorMessage, setErrorMessage] = useState("");
  const { loading, onMutateAuth, renderErrorMessage, error: authError } = useAuthLogin();
  const [authType, setAuthType] = useState<"phone" | "email">("phone");
  const [phoneValue, setPhoneValue] = useState("");
  const [emailValue, setEmailValue] = useState("");

  const handleCountryChange = (value: string) => {
    if (value) {
      setCountry(value);
    }
  };

  const onSubmit = async (data: ILoginFieldInput) => {
    setErrorMessage("");

    const login = authType === "phone" ? phoneValue.replace("+", "") : emailValue.trim();

    try {
      const res = await onMutateAuth({
        ...data,
        login,
        countryCode: country.toUpperCase(),
        type: authType,
      });

      if (res?.success) {
        user.setToken(res?.token);

        const _user = await getProfile();
        user.setIsAuth(true);
        user.setUser(_user);
        user.setUserBalance(_user.balance.amount);

        modal.closeModal();
      }
    } catch (error: any) {
      setErrorMessage(error.messages);
    }
  };

  return (
    <div className={styles.form_wrapper}>
      <div className={styles.header}>
        <div className={styles.title}>{t("login")}</div>
        <button className={styles.state_btn} onClick={() => onShowTab("registration")}>
          <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="100%">
            <use href="/images/sprite.svg#sign-up"></use>
          </svg>
          {t("registration")}
        </button>
      </div>

      <ToggleEmailPhone
        onChange={(value) => {
          setAuthType(value);
          setValue("login", value === "phone" ? phoneValue : emailValue);
        }}
      />

      <form
        className={styles.form}
        onSubmit={(e) => {
          handleSubmit(onSubmit)(e);
        }}
      >
        <div className={styles.form_field}>
          {authType === "phone" && (
            <>
              <label className={cx(styles.label, styles.phone_label)}>
                <Controller
                  name="login"
                  control={control}
                  rules={{ required: tValidation("required") }}
                  render={({ field: { onChange } }) => (
                    <PhoneInput
                      className={styles.phone_input}
                      autoComplete={"off"}
                      value={phoneValue}
                      onChange={(_phone, _country) => {
                        setPhoneValue(_phone);
                        onChange(_phone);
                        handleCountryChange(_country);
                      }}
                      label={false}
                    />
                  )}
                />
              </label>

              <div className={styles.message}>{renderErrorMessage("phone", authError || [])}</div>
            </>
          )}

          {authType === "email" && (
            <>
              <label className={styles.label}>
                <input
                  {...register("login", {
                    required: tValidation("required"),
                    onChange: (e) => setEmailValue(e.target.value),
                  })}
                  className={styles.input}
                  type="email"
                  value={emailValue}
                  placeholder=""
                />
                <span className={styles.label_value}>E-mail</span>
              </label>

              <div className={styles.message}>{renderErrorMessage("email", authError || [])}</div>
            </>
          )}

          <div className={styles.message}>{renderErrorMessage("login", authError || [])}</div>
          {errors.login && <div className={styles.message}>{errors.login.message}</div>}
          <div className={styles.message}>{renderErrorMessage("countryCode", authError || [])}</div>
        </div>

        <div className={styles.form_field}>
          <label className={styles.label}>
            <input
              {...register("password", {
                required: tValidation("required"),
                minLength: { value: 6, message: tValidation("password_min_length") },
              })}
              className={styles.input}
              type="password"
              placeholder=""
              id="password"
            />
            <span className={styles.label_value}>{t("password")}</span>
          </label>
          {errors.password && <div className={styles.message}>{errors.password.message}</div>}
        </div>

        {errorMessage && <div className={styles.error_message}>{errorMessage}</div>}
        <div className={styles.message}>{renderErrorMessage("password", authError || [])}</div>

        <div className={styles.form_field}>
          <button className={styles.btn_submit} type="submit" disabled={loading}>
            {t("login")}
            {loading && "..."}
          </button>
        </div>

        <div className={styles.form_field}>
          <button
            className={styles.password_recovery}
            onClick={() => onShowTab("recoveryPassword")}
          >
            {t("forgot_password")}
          </button>
        </div>

        <div className={styles.form_field}>
          <p className={styles.description}>
            {t.rich("rulesDesk", {
              link_1: (chunks) => (
                <Link href={"/terms-and-conditions"} target="_blank" rel="noopener noreferrer">
                  {chunks}
                </Link>
              ),
              link_2: (chunks) => (
                <Link href={"/privacy-policy"} target="_blank" rel="noopener noreferrer">
                  {chunks}
                </Link>
              ),
            })}
          </p>
        </div>
      </form>
    </div>
  );
}
